import * as Icons from "@mui/icons-material";
import TerminalIcon from "@mui/icons-material/Terminal";
import { Box, IconButton, ListItemText, Menu, MenuItem, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Bowser from "bowser";
import React, { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DrawerHeader } from "@/components/DrawerHeader";
import ReadOnlyModeToggle from "@/components/ReadOnlyModeToggle";
import ToggleSecondWindowButton from "@/components/SecondWindow/ToggleSecondWindowButton.tsx";
import SqlEditor from "@/components/SqlEditor.tsx";
import Locator from "@/locator";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UxContext } from "@/models/UxStateProvider";
import LanguageDropdown from "../LanguageDropdown";
import WorkspaceMenuSelect from "../WorkspaceMenuSelect";

function WorkspaceDrawerHeader() {
  const toggleSqlEditor = () => setSqlEditorOpen((value) => !value);
  const { flags } = useContext(AppContext);
  const browser = Bowser.getParser(window.navigator.userAgent);
  const searchKey = browser.getOSName().toLowerCase() === "macos" ? "Cmd" : "Ctrl";
  const { isSmUp, toggleLeftNav, setUserFeedbackModalOpen, setGlobalSearchOpen, setRightNavOpen } =
    useContext(UxContext);
  const { isWorkspaceAdmin, currentWorkspaceId } = useContext(WorkspaceContext);
  const { ampli } = useContext(TrackingContext);
  const theme = useTheme();
  const { readOnlyMode, showDebugMenu } = flags;
  const showReadOnlyModeToggle = readOnlyMode || showDebugMenu;
  const navigate = useNavigate();
  const workspaceAdmin = isWorkspaceAdmin();
  const [workspaceSettingsAnchorEl, setWorkspaceSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [sqlEditorOpen, setSqlEditorOpen] = useState(false);
  const workspaceSettingsMenuOpen = Boolean(workspaceSettingsAnchorEl);

  const globalSearch = true;

  const closeWorkspaceSettingsMenu = () => {
    setWorkspaceSettingsAnchorEl(null);
  };

  const openWorkspaceSettingsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setWorkspaceSettingsAnchorEl(event.currentTarget);
  };

  const goToUserManagement = useCallback(() => {
    ampli.manageUsers();
    setRightNavOpen(false);
    closeWorkspaceSettingsMenu();
    setTimeout(() => navigate(`/workspaces/${currentWorkspaceId}/user-management`), 200);
  }, [currentWorkspaceId, ampli, navigate, setRightNavOpen, closeWorkspaceSettingsMenu]);

  return (
    <>
      <DrawerHeader position="top" sx={{ p: 0 }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
          <Box sx={{ flex: 1, justifyItems: "start", display: "flex" }}>
            <WorkspaceMenuSelect />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {globalSearch && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip
                  componentsProps={{
                    tooltip: {
                      sx: {
                        background: theme.palette.brand.other.gray.dark,
                      },
                    },
                  }}
                  title={
                    <Box
                      sx={{
                        fontSize: "16px",
                      }}
                    >
                      Search <strong>({searchKey}+K)</strong>
                    </Box>
                  }
                >
                  <IconButton
                    aria-label={`Search (${searchKey}+K)`}
                    sx={{
                      background: "transparent",
                      borderRadius: "100%",
                    }}
                    onClick={() => setGlobalSearchOpen(true)}
                  >
                    <Icons.Search role="img" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                aria-label={Locator.mainNav.feedbackButton}
                onClick={() => setUserFeedbackModalOpen(true)}
                sx={{
                  background: "transparent",
                  borderRadius: "1000px",
                  height: 44,
                  width: 44,
                }}
              >
                <Icons.Help role="presentation" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LanguageDropdown />
            </Box>
            {flags.sqlEditor && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  sx={{
                    background: "transparent",
                    borderRadius: "100%",
                    ml: 0,
                    height: 44,
                    width: 44,
                  }}
                  onClick={toggleSqlEditor}
                >
                  <TerminalIcon role="img" />
                </IconButton>
              </Box>
            )}
            <ToggleSecondWindowButton />
            {(currentWorkspaceId && workspaceAdmin) || showReadOnlyModeToggle ? (
              <>
                <IconButton
                  sx={{
                    background: "transparent",
                    borderRadius: "100%",
                  }}
                  color="secondary"
                  aria-label={Locator.workspaceNav.optionsMenu.button}
                  onClick={openWorkspaceSettingsMenu}
                >
                  <Icons.PendingOutlined role="img" />
                </IconButton>
                <Menu
                  anchorEl={workspaceSettingsAnchorEl}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  sx={{
                    mt: 1,
                  }}
                  open={workspaceSettingsMenuOpen}
                  onClose={closeWorkspaceSettingsMenu}
                >
                  {showReadOnlyModeToggle && (
                    <MenuItem>
                      <ReadOnlyModeToggle setActive={() => {}} sx={{ height: "30px" }} type="switch" />
                    </MenuItem>
                  )}
                  {currentWorkspaceId && workspaceAdmin && (
                    <Box>
                      <MenuItem
                        onClick={goToUserManagement}
                        key="workspace-members"
                        aria-label={Locator.workspaceNav.optionsMenu.members}
                      >
                        <ListItemText primaryTypographyProps={{ fontWeight: 500 }}>Manage members</ListItemText>
                      </MenuItem>
                    </Box>
                  )}
                </Menu>
              </>
            ) : null}
          </Box>
        </Box>
        {!isSmUp && (
          <IconButton color="primary" aria-label="close drawer" onClick={toggleLeftNav}>
            <Icons.Close role="img" />
          </IconButton>
        )}
      </DrawerHeader>
      <SqlEditor open={sqlEditorOpen} close={() => toggleSqlEditor()} />
    </>
  );
}

export default WorkspaceDrawerHeader;
